import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Square, RotateCcw, Check } from "lucide-react";

const MAX_SECONDS = 120;

export const AudioRecorder = ({ onRecorded, countdownSeconds = 3 }) => {
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | ready | countdown | recording | done
  const [error, setError] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => { stopStream(); clearInterval(timerRef.current); clearInterval(countdownRef.current); }, []);

  const startMic = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setStatus("ready");
    } catch {
      setError("Não foi possível acessar o microfone. Verifique as permissões do navegador.");
    }
  }, []);

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const beginRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    let mime = "audio/webm;codecs=opus";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "audio/mp4";
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime.split(";")[0] });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setStatus("done");
      stopStream();
      onRecorded(blob);
    };
    recorder.start();
    setSeconds(0);
    setStatus("recording");
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stopRecording();
        return s + 1;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    const cd = Number.isFinite(countdownSeconds) ? countdownSeconds : 3;
    if (!cd || cd <= 0) {
      beginRecording();
      return;
    }
    clearInterval(countdownRef.current);
    setCountdown(cd);
    setStatus("countdown");
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          beginRecording();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    onRecorded(null);
    setStatus("idle");
    startMic();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-4">
      {error && (
        <div data-testid="mic-error" className="p-4 border border-red-200 bg-red-50 text-red-700 text-sm rounded-md">
          {error}
        </div>
      )}

      {status === "idle" && (
        <button
          data-testid="enable-mic-btn"
          onClick={startMic}
          className="w-full py-8 border-2 border-dashed border-zinc-300 rounded-lg flex flex-col items-center gap-3 text-zinc-600 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors"
        >
          <Mic size={32} />
          <span className="font-semibold">Ativar microfone</span>
          <span className="text-xs text-zinc-400">O navegador vai pedir permissão de microfone</span>
        </button>
      )}

      {(status === "ready" || status === "countdown" || status === "recording") && (
        <div className="bg-white border border-zinc-200 rounded-lg py-10 flex flex-col items-center gap-4 relative overflow-hidden">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
            status === "recording" ? "bg-red-50" : status === "countdown" ? "bg-orange-100" : "bg-orange-50"
          }`}>
            <Mic size={32} className={
              status === "recording"
                ? "text-red-500 rec-dot"
                : status === "countdown"
                  ? "text-[#FF4F00] animate-pulse"
                  : "text-[#FF4F00]"
            } />
          </div>
          {status === "countdown" && (
            <div
              data-testid="audio-countdown-number"
              key={countdown}
              className="text-[#FF4F00] font-black tracking-tighter select-none animate-countdown-pulse leading-none"
              style={{ fontSize: "clamp(4rem, 14vw, 7rem)" }}
            >
              {countdown}
            </div>
          )}
          {status === "recording" && (
            <div className="flex items-center gap-2">
              <span className="rec-dot w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="font-semibold text-sm" data-testid="audio-rec-timer">{fmt(seconds)}</span>
            </div>
          )}
          {status === "ready" && (
            <p className="text-sm text-zinc-500">Microfone pronto. Fale à vontade quando começar.</p>
          )}
        </div>
      )}

      {status === "done" && previewUrl && (
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <audio src={previewUrl} controls className="w-full" data-testid="audio-preview" />
        </div>
      )}

      <div className="flex gap-3">
        {status === "ready" && (
          <button
            data-testid="start-audio-recording-btn"
            onClick={startRecording}
            className="flex-1 py-3.5 bg-[#FF4F00] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#E64600] transition-colors"
          >
            <span className="w-3 h-3 rounded-full bg-white" /> Gravar
          </button>
        )}
        {status === "countdown" && (
          <button
            data-testid="countdown-audio-recording-btn"
            disabled
            className="flex-1 py-3.5 bg-[#FF4F00]/70 text-white rounded-full font-semibold flex items-center justify-center gap-2 cursor-wait"
          >
            <span className="w-3 h-3 rounded-full bg-white animate-pulse" /> Começando em {countdown}...
          </button>
        )}
        {status === "recording" && (
          <button
            data-testid="stop-audio-recording-btn"
            onClick={stopRecording}
            className="flex-1 py-3.5 bg-[#0A0A0A] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors"
          >
            <Square size={16} className="fill-white" /> Parar gravação
          </button>
        )}
        {status === "done" && (
          <button
            data-testid="rerecord-audio-btn"
            onClick={reset}
            className="flex-1 py-3 border border-zinc-300 rounded-full font-semibold flex items-center justify-center gap-2 text-zinc-700 hover:border-zinc-500 transition-colors"
          >
            <RotateCcw size={16} /> Regravar
          </button>
        )}
        {status === "done" && (
          <div className="flex-1 py-3 rounded-full font-semibold flex items-center justify-center gap-2 text-green-700 bg-green-50 border border-green-200">
            <Check size={16} /> Áudio pronto
          </div>
        )}
      </div>
    </div>
  );
};
